"use client";

/**
 * Navigation-timing beacon — the tripwire that keeps clicks fast.
 *
 * Measures what the user actually feels on every in-app navigation:
 *   commit_ms — click → URL change (the "did anything happen?" wait; slow here
 *               means the route wasn't warm and the click paid the network
 *               before Next.js would commit the URL)
 *   paint_ms  — click → first frame after the new route rendered
 *
 * Reported to PostHog as `nav_timing` with route PATTERNS (ids stripped), so
 * a dashboard can watch p75 per route and regressions surface as data instead
 * of founder pain. A route trending slow means it lost its warm path: it needs
 * a real <Link>, or a slot in WARM_ROUTES, or something re-tainted the shell.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { posthog } from "@/lib/posthog";

/** Collapse id-ish segments so events aggregate by route shape, not by record. */
export function normalizeRoute(path: string): string {
  const clean = path.split(/[?#]/)[0];
  const collapsed = clean
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          seg
        )
      )
        return ":id";
      if (/^[0-9a-f]{12,}$/i.test(seg)) return ":id";
      if (/^\d+$/.test(seg)) return ":id";
      return seg;
    })
    .join("/");
  return collapsed || "/";
}

interface PendingClick {
  /** In-app anchor pathname when the click hit a link; null for other clicks (programmatic navs). */
  href: string | null;
  at: number;
}

/** A click only explains a URL change that follows it promptly. */
export const CLICK_ATTRIBUTION_WINDOW_MS = 10_000;

export function classifyNav(
  pending: PendingClick | null,
  committedAt: number
): { source: "link" | "programmatic"; commitMs: number } | null {
  if (!pending) return null;
  const commitMs = committedAt - pending.at;
  if (commitMs < 0 || commitMs > CLICK_ATTRIBUTION_WINDOW_MS) return null;
  return { source: pending.href ? "link" : "programmatic", commitMs };
}

/** Extract the in-app pathname from a click's nearest anchor, if any. */
export function anchorPathFromClick(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest("a[href]");
  if (!anchor) return null;
  const href = anchor.getAttribute("href") ?? "";
  if (!href.startsWith("/")) return null;
  return href.split(/[?#]/)[0];
}

export function useNavTiming() {
  const pathname = usePathname();
  const pendingRef = useRef<PendingClick | null>(null);
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      pendingRef.current = {
        href: anchorPathFromClick(e.target),
        at: performance.now(),
      };
    };
    // Capture phase: run before any handler that might stopPropagation.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    if (prev === null || prev === pathname) return; // initial load, not a navigation

    const committedAt = performance.now();
    const nav = classifyNav(pendingRef.current, committedAt);
    pendingRef.current = null;
    if (!nav) return; // no recent click to attribute (back/forward, redirects)

    // Two frames ≈ the new route's first paint.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        posthog.capture("nav_timing", {
          to: normalizeRoute(pathname),
          from: normalizeRoute(prev),
          source: nav.source,
          commit_ms: Math.round(nav.commitMs),
          paint_ms: Math.round(
            performance.now() - (committedAt - nav.commitMs)
          ),
        });
      });
    });
  }, [pathname]);
}
